import React, { useState } from "react";
import { Copy, Check, Lock } from "lucide-react";
import { buildUniversalExtractUrl, buildCurlSnippet, buildFetchSnippet, copyTextToClipboard } from "../../constants";
import { SX } from "../studioTokens";

const OVERLINE = "text-[11px] font-semibold uppercase tracking-[0.06em] text-[#8A8A90]";
const COPY_BTN = "inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11.5px] font-semibold bg-transparent hover:bg-[#F5F5F5] flex-shrink-0 transition-colors";

/* The old workspace's API Access popover (extraction endpoint + curl/fetch snippets).
   The endpoint needs a saved slug, so for an unsaved draft it prompts to save first. */
const ApiUsage = ({ slug }) => {
  const [mode, setMode] = useState("curl");
  const [copied, setCopied] = useState(null);

  const copy = (text, key) => { copyTextToClipboard(text).catch(() => {}); setCopied(key); setTimeout(() => setCopied(null), 1500); };

  if (!slug) {
    return (
      <div className={`absolute top-[calc(100%+8px)] right-0 w-[340px] p-[18px] z-[200] text-left ${SX.popover}`}>
        <div className={`${OVERLINE} pb-2.5 border-b border-[#ECE9EE]`}>Universal Extract API</div>
        <div className="flex items-start gap-2 mt-3.5 text-[12.5px] text-[#52525B] leading-[1.55]">
          <Lock size={14} className="mt-px flex-shrink-0 text-[#8A8A90]" />
          Save the template to get its extraction endpoint and ready-to-use curl / fetch snippets.
        </div>
      </div>
    );
  }

  const url = buildUniversalExtractUrl(slug);
  const snippet = mode === "curl" ? buildCurlSnippet(slug) : buildFetchSnippet(slug);

  return (
    <div className={`absolute top-[calc(100%+8px)] right-0 w-[420px] max-w-[min(420px,calc(100vw-2rem))] p-[18px] z-[200] text-left ${SX.popover}`}>
      <div className={`${OVERLINE} pb-2.5 border-b border-[#ECE9EE]`}>Universal Extract API</div>
      <p className="m-0 mt-3.5 mb-4 text-[12.5px] leading-[1.55] text-[#52525B]">Post a file to this endpoint with your saved template slug to reuse this template anywhere.</p>

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <span className="text-[12px] font-semibold text-[#1A1A1A]">Endpoint</span>
          <code className="block mt-1.5 px-2.5 py-2 rounded-lg bg-[#FBF5FA] text-[#893F82] text-[12px] font-mono leading-[1.5] break-all">{url}</code>
        </div>
        <button className={`${COPY_BTN} ${copied === "url" ? "text-[#893F82]" : "text-[#8A8A90]"}`} onClick={() => copy(url, "url")}>
          {copied === "url" ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
        </button>
      </div>

      <div className="mt-4 border-t border-[#ECE9EE] pt-3.5">
        <div className="flex items-center gap-1 bg-[#F5F5F5] rounded-[10px] p-1 w-fit mb-2.5">
          {["curl", "fetch"].map((m) => (
            <button key={m} className={`text-[12.5px] font-semibold px-3 py-1.5 rounded-lg transition-colors ${mode === m ? "bg-white text-[#1A1A1A] shadow-[0_1px_3px_rgba(0,0,0,0.08)]" : "text-[#52525B]"}`} onClick={() => setMode(m)}>{m}</button>
          ))}
          <button className={`ml-2 ${COPY_BTN} ${copied === "snippet" ? "text-[#893F82]" : "text-[#8A8A90]"}`} onClick={() => copy(snippet, "snippet")}>
            {copied === "snippet" ? <><Check size={12} /> Copied</> : <><Copy size={12} /> Copy</>}
          </button>
        </div>
        <pre className="m-0 p-3.5 rounded-[12px] bg-[#1A1A1A] text-[#E9D2E6] text-[11.5px] leading-[1.7] font-mono whitespace-pre-wrap break-words max-h-[220px] overflow-auto">{snippet}</pre>
      </div>
    </div>
  );
};

export default ApiUsage;
